/**
 * Content hashing, which is how two sessions decide they hold the same model.
 *
 * SHA-256 over the raw file bytes, and nothing else — not the name, not the size, not the
 * triangle count. A name is a label somebody typed and two people will use the same one
 * for different things; the bytes are the model.
 *
 * The consequence to be aware of runs the other way: this says nothing about geometry.
 * The same object exported twice by a slicer differs in its header, its float rounding, or
 * merely its facet order, and so counts as two models. That is the honest answer to "is
 * this the same file?", which is the question being asked, rather than a guess at "is this
 * the same shape?", which would need a tolerance and would sometimes be wrong.
 *
 * `crypto.subtle` needs a secure context, which is also what WebXR and the account cookie
 * need, so it is always present where any of this matters.
 */

import type { ModelHash } from './sync.js';

/** Lowercase hex SHA-256 of the given bytes. */
export async function hashBytes(bytes: BufferSource): Promise<ModelHash> {
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return toHex(new Uint8Array(digest));
}

function toHex(bytes: Uint8Array): string {
  let hex = '';
  for (const byte of bytes) hex += byte.toString(16).padStart(2, '0');
  return hex;
}

/** True if `value` could be something this produced. Guards data arriving from a peer. */
export function isModelHash(value: unknown): value is ModelHash {
  return typeof value === 'string' && /^[0-9a-f]{64}$/.test(value);
}

/** The first few characters, for logs and messages where the whole thing is noise. */
export function shortHash(hash: ModelHash): string {
  return hash.slice(0, 8);
}
